import { Dispatch, SetStateAction, useCallback } from "react";

import { FaxDirectorySearchResultItem } from "../Components/Search/FaxDirectorySearchResultItem";
import { DirectoryFaxNumber } from "../Models/DirectoryFaxNumber";
import { DirectoryHealthCareProvider } from "../Models/DirectoryHealthCareProvider";
import { DirectoryOtherEstablishment } from "../Models/DirectoryOtherEstablishment";

interface OrganizationSectionEntityStateActions {
    setEntity: (updatedFields: Partial<DirectoryHealthCareProvider>) => void;
    setEditedEstablishmentId: Dispatch<SetStateAction<string>>;
    setShowAddDirectoryPopup: Dispatch<SetStateAction<boolean>>;
}

export const useOrganizationSectionEntity = (
    entity: DirectoryHealthCareProvider,
    stateActions: OrganizationSectionEntityStateActions
): (item: FaxDirectorySearchResultItem) => void => {
    const { setEditedEstablishmentId, setEntity, setShowAddDirectoryPopup } = stateActions;

    const getFaxNumber = useCallback(
        (establishment: DirectoryOtherEstablishment, faxNumberId?: string | null): DirectoryFaxNumber | null => {
            const { faxNumbers } = establishment;

            const selectedFaxNumber = faxNumbers.firstOrDefault(p => p.id === faxNumberId);
            if (faxNumberId && selectedFaxNumber && !entity.linkedFaxNumbers.some(p => p.id === faxNumberId)) {
                return selectedFaxNumber;
            }

            const defaultFaxNumber = establishment.getDefaultFaxNumberToSelect();
            if (defaultFaxNumber && !defaultFaxNumber.isDuplicate(entity.linkedFaxNumbers)) {
                return defaultFaxNumber;
            }

            return faxNumbers
                .sortByProperty(p => p.id)
                .firstOrDefault(fax => !fax.isDuplicate(entity.linkedFaxNumbers));
        },
        [entity.linkedFaxNumbers]
    );

    const handleSelect = useCallback(
        (establishment: DirectoryOtherEstablishment, fax: DirectoryFaxNumber): void => {
            const updatedEstablishments = entity.linkedEstablishments.length === 1 && !entity.linkedEstablishments?.firstOrDefault()?.id
                ? [establishment]
                : [...entity.linkedEstablishments, establishment];

            const updatedFaxNumbers = fax?.id
                ? [...entity.linkedFaxNumbers, fax]
                : [...entity.linkedFaxNumbers];

            if (fax?.id) {
                updatedEstablishments[updatedEstablishments.length - 1].selectedLinkedFaxNumber = fax;
            }

            setEntity({
                linkedEstablishments: updatedEstablishments,
                linkedFaxNumbers: updatedFaxNumbers,
                faxNumber: new DirectoryFaxNumber({ ...entity.faxNumber, number: "" }),
                establishmentLinkedToRawInformationId: establishment.id
            });

            setEditedEstablishmentId(establishment.id);
        },
        [entity, setEntity, setEditedEstablishmentId]
    );

    const onSelectEntity = useCallback(
        (item: FaxDirectorySearchResultItem): void => {
            const establishment = item.entity as DirectoryOtherEstablishment;
            const faxNumber = getFaxNumber(establishment, item.selectedFaxNumberId);

            handleSelect(
                {
                    ...establishment,
                    selectedLinkedFaxNumber: faxNumber
                } as DirectoryOtherEstablishment,
                faxNumber
            );
            setShowAddDirectoryPopup(false);
        },
        [getFaxNumber, handleSelect, setShowAddDirectoryPopup]
    );

    return onSelectEntity;
};
